/**
 * @component LineChart
 * @description A responsive line chart component that displays time-series data with customizable animations,
 * loading states, tooltips, and visual styles. Supports different display modes for yields or prices.
 * Supports both single-line and multi-line charts through the series prop.
 *
 * @hierarchy
 * LineChart
 * ├── ChartContainer
 * │   ├── Skeleton (loading UI)
 * │   │   └── ChartSkeletonContainer
 * │   │       └── SkeletonBar (animated loading bars)
 * │   └── ResponsiveContainer (recharts)
 * │       └── RechartsLineChart (recharts)
 * │           ├── Customized with NoData (empty state display)
 * │           ├── CartesianGrid (background grid lines)
 * │           ├── YAxis with CustomYAxisTick (formatted value display)
 * │           ├── XAxis with CustomXAxisTick (date/time display)
 * │           ├── Tooltip with TooltipWithFallback (hover data display)
 * │           └── Line with CustomizedDot (animated data visualization)
 *
 * @helpers (see helpers.tsx)
 * ├── calculateDigits - Calculates appropriate decimal precision based on data range
 * ├── checkHasNoData - Determines if chart has data to display
 * ├── calculateXAxisInterval - Ensures no more than 8 X-axis ticks
 * ├── getLineSeries - Normalizes single and multiple series configurations
 * ├── getActiveDotProps - Returns hover dot configuration
 * ├── getDotProps - Returns dot configuration based on animation settings
 *
 * @hooks (see hooks/use-chart-transition.ts)
 * └── useChartTransition - Manages chart transition state for smooth animations
 *
 * @example
 * <LineChart
 *   data={[{ x: '2023-01-01', y: 1.25 }, { x: '2023-01-02', y: 1.35 }]}
 *   height={348}
 *   displayMode="yield"
 *   showGrid={true}
 *   tooltipFormatter={(dataPoint) => <CustomTooltip dataPoint={dataPoint} />}
 *   useFastAnimation={false}
 *   onAnimationEnd={() => console.log('Animation complete')}
 * />
 */

'use client'

import type { CurveType } from 'recharts/types/shape/Curve'

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  CartesianGrid,
  Customized,
  Line,
  LineChart as RechartsLineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { cn } from '@/utils/shadcn/utils'

import { CHART_MARGINS, CHART_OFFSETS, CHART_STYLES } from './constants'
import {
  calculateDigits,
  calculateXAxisInterval,
  checkHasNoData,
  getActiveDotProps,
  getDotProps,
  getLineSeries,
} from './helpers'
import { useChartTransition } from './hooks/use-chart-transition'
import styles from './line-chart.module.css'
import {
  ChartContainer,
  CustomXAxisTick,
  CustomYAxisTick,
  EmptyState,
  Skeleton,
  TooltipWithFallback,
} from './styles'
import { CURVE_TYPES, type LineChartProps } from './types'

function LineChart({
  data,
  series,
  width = '100%',
  height = 348,
  tooltipFormatter,
  showGrid = true,
  showDots = false,
  displayMode = 'yield',
  interval = 21,
  isPricesLoading = false,
  isFetching = false,
  useFastAnimation = false,
  onAnimationEnd,
  curveType = CURVE_TYPES.LINEAR,
  xAxisFormatter,
  xAxisTicks,
  yAxisTicks,
  customYAxisTick,
  showXAxisBorder = false,
  xAxisBorderPosition = 90,
  yAxisDomain = ['auto', 'auto'],
  yAxisTickCount = 5,
  connectNulls = false,
  freezeDataDelay = 0,
  freezeYAxisDomain = true,
  fadeStart = true,
  showYAxis = true,
}: LineChartProps) {
  const { isTransitioning, startTransition } = useChartTransition()

  const shouldFreeze = freezeDataDelay > 0
  const [frozenData, setFrozenData] = useState(data)
  const [frozenYAxisDomain, setFrozenYAxisDomain] = useState(yAxisDomain)
  const [frozenYAxisTicks, setFrozenYAxisTicks] = useState(yAxisTicks)
  const [frozenXAxisInterval, setFrozenXAxisInterval] = useState(
    calculateXAxisInterval(interval, data.length),
  )

  // Freeze data while fetching new (e.g. percentile change)
  useEffect(() => {
    if (shouldFreeze && !isFetching) {
      const timeout = setTimeout(() => {
        setFrozenData(data)
        setFrozenYAxisDomain(yAxisDomain)
        setFrozenYAxisTicks(yAxisTicks)
        setFrozenXAxisInterval(calculateXAxisInterval(interval, data.length))
      }, freezeDataDelay)

      return () => clearTimeout(timeout)
    }
  }, [
    shouldFreeze,
    isFetching,
    data,
    yAxisDomain,
    yAxisTicks,
    interval,
    freezeDataDelay,
  ])

  const activeData = shouldFreeze ? frozenData : data
  const shouldFreezeYAxis = shouldFreeze && freezeYAxisDomain
  const activeYAxisDomain = shouldFreezeYAxis ? frozenYAxisDomain : yAxisDomain
  const activeYAxisTicks = shouldFreezeYAxis ? frozenYAxisTicks : yAxisTicks
  const activeXAxisInterval = shouldFreeze
    ? frozenXAxisInterval
    : calculateXAxisInterval(interval, data.length)

  const hasMultipleSeries = Boolean(series && series.length > 0)

  const digits = useMemo(
    () => calculateDigits(series, activeData, hasMultipleSeries),
    [activeData, series, hasMultipleSeries],
  )

  const hasNoData = useMemo(
    () => checkHasNoData(series, activeData, hasMultipleSeries),
    [activeData, series, hasMultipleSeries],
  )

  const containerRef = useRef<HTMLDivElement>(null)
  const [isContainerReady, setIsContainerReady] = useState(false)

  // Wait for container to be measured before showing skeleton
  useLayoutEffect(() => {
    const timer = setTimeout(() => setIsContainerReady(true), 0)
    return () => clearTimeout(timer)
  }, [])

  const animationDuration = useFastAnimation
    ? CHART_STYLES.animation.duration.morphLine
    : CHART_STYLES.animation.duration.drawLine

  const lineSeries = useMemo(
    () => getLineSeries(series, activeData),
    [series, activeData],
  )

  // Normalize showGrid to GridConfig
  const gridConfig = useMemo(() => {
    if (typeof showGrid === 'boolean') {
      return { vertical: false, horizontal: showGrid }
    }
    return {
      vertical: showGrid.vertical ?? true,
      horizontal: showGrid.horizontal ?? true,
    }
  }, [showGrid])

  const shouldShowGrid = gridConfig.vertical || gridConfig.horizontal

  return (
    <ChartContainer
      ref={containerRef}
      className={cn(
        styles['line-chart'],
        fadeStart ? styles['fade-start'] : '',
      )}
    >
      <Skeleton
        isFetching={isFetching && isContainerReady}
        containerRef={containerRef}
      />

      <ResponsiveContainer
        width={width}
        height={height}
        debounce={100}
        className={`${isFetching ? '' : styles.visible}`}
      >
        <RechartsLineChart data={activeData} margin={CHART_MARGINS}>
          {hasNoData && !isPricesLoading ? (
            <Customized component={EmptyState} />
          ) : (
            <>
              {shouldShowGrid && (
                <CartesianGrid
                  vertical={gridConfig.vertical}
                  horizontal={gridConfig.horizontal}
                  stroke="var(--cartesian-grid-stroke)"
                  horizontalCoordinatesGenerator={(props) =>
                    CHART_OFFSETS.getHorizontalOffsets(props.height)
                  }
                />
              )}
              {showYAxis && (
                <YAxis
                  domain={activeYAxisDomain}
                  axisLine={false}
                  tickLine={false}
                  orientation="left"
                  width={1}
                  padding={CHART_STYLES.padding.yAxis}
                  tick={
                    customYAxisTick || (
                      <CustomYAxisTick
                        displayMode={displayMode}
                        digits={digits}
                      />
                    )
                  }
                  tickMargin={-6}
                  tickCount={activeYAxisTicks ? undefined : yAxisTickCount}
                  ticks={activeYAxisTicks}
                  type="number"
                />
              )}
              {showXAxisBorder && (
                <line
                  x1="48"
                  y1={`${xAxisBorderPosition}%`}
                  x2="100%"
                  y2={`${xAxisBorderPosition}%`}
                  stroke="var(--cartesian-grid-stroke)"
                  strokeWidth="1"
                />
              )}
              <XAxis
                dataKey="x"
                axisLine={false}
                tickLine={false}
                padding={CHART_STYLES.padding.xAxis}
                domain={['dataMin', 'dataMax']}
                tick={
                  <CustomXAxisTick
                    isTransitioning={isTransitioning}
                    formatter={xAxisFormatter}
                  />
                }
                interval={xAxisTicks ? 0 : activeXAxisInterval}
                ticks={xAxisTicks}
              />
              <Tooltip
                content={
                  <TooltipWithFallback
                    tooltipFormatter={tooltipFormatter}
                    series={series}
                  />
                }
                animationDuration={CHART_STYLES.animation.duration.tooltip}
                cursor={
                  !isFetching && {
                    stroke: 'var(--line-chart-stroke)',
                    strokeWidth: 1,
                  }
                }
              />

              {!isPricesLoading &&
                !isFetching &&
                lineSeries.map((line, index) => (
                  <Line
                    key={`${line.name}-${line.hide ? 'hidden' : 'visible'}`}
                    type={curveType as CurveType}
                    dataKey={line.dataKey}
                    stroke={line.stroke}
                    name={line.name}
                    hide={line.hide}
                    strokeOpacity={line.strokeOpacity ?? 1}
                    activeDot={line.activeDot ?? getActiveDotProps(line.stroke)}
                    isAnimationActive
                    animationEasing="ease"
                    animationDuration={animationDuration}
                    strokeWidth={1.5}
                    connectNulls={connectNulls}
                    dot={getDotProps(
                      useFastAnimation,
                      showDots,
                      line.data,
                      activeData,
                      line.stroke,
                    )}
                    onAnimationEnd={index === 0 ? onAnimationEnd : undefined}
                    onAnimationStart={
                      index === 0 && useFastAnimation
                        ? startTransition
                        : undefined
                    }
                  />
                ))}
            </>
          )}
        </RechartsLineChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default LineChart
